import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Button,
  LinearProgress, Fade, Avatar, Divider, Alert
} from '@mui/material';
import {
  LockClock as LockClockIcon,
  Security as SecurityIcon,
  Login as LoginIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';

// ─── Event names (exported so refreshManager / AuthContext can import them) ──
export const SESSION_EXPIRED_EVENT  = 'session-expired';
export const SESSION_IDLE_EVENT     = 'session-idle-warning';
// Fired by AuthContext after a genuinely established new login so other tabs
// can dismiss their expiry dialogs and reload to get fresh server state.
export const SESSION_RESTORED_EVENT = 'session-restored';

// Distinct message types on the BroadcastChannel — logout must never be
// confused with session restoration.
const BC_TYPE_EXPIRED  = 'SESSION_EXPIRED';
const BC_TYPE_RESTORED = 'SESSION_RESTORED';

// A stable per-tab identifier used to skip messages we broadcast ourselves
// (BroadcastChannel does not echo to the sender, but we track it defensively).
const TAB_ID = Math.random().toString(36).slice(2);

// ─── Return-URL security ──────────────────────────────────────────────────────
// Blocked URL prefixes (checked on BOTH the raw and decoded strings).
const BLOCKED_PREFIXES = [
  'http:', 'https:', '//', 'javascript:', 'data:', 'vbscript:', 'file:',
];

/**
 * Validate and sanitize a return path before storing or using it for navigation.
 *
 * Acceptance criteria (verified against these inputs):
 *   /dashboard                   → accepted ✓
 *   /sales/invoices?page=2#top   → accepted ✓
 *   //attacker.example           → rejected (protocol-relative)
 *   https://attacker.example     → rejected (absolute URL)
 *   javascript:alert(1)          → rejected (javascript: URI)
 *   /%2F%2Fattacker.example      → rejected (encoded // after decode)
 *   /login                       → rejected (login loop)
 *   (empty / null)               → rejected
 *
 * @param {string|null} raw  - Raw value from sessionStorage or window.location
 * @returns {string|null}    - Validated original string, or null if unsafe
 */
export function sanitizeReturnPath(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const lower = trimmed.toLowerCase();

  // --- Round 1: check raw string before any decoding ---
  if (BLOCKED_PREFIXES.some(p => lower.startsWith(p))) return null;

  // --- Round 2: decode percent-encoded characters and re-check ---
  // This catches /%2F%2Fattacker.example → //attacker.example
  let decoded;
  try {
    decoded = decodeURIComponent(trimmed);
  } catch {
    // Malformed percent-encoding — reject outright
    return null;
  }

  const decodedLower = decoded.toLowerCase();
  if (BLOCKED_PREFIXES.some(p => decodedLower.startsWith(p))) return null;

  // --- Round 3: structural checks on the decoded path ---
  // Must start with exactly one slash (relative internal path)
  if (!decoded.startsWith('/') || decoded.startsWith('//')) return null;

  // No control characters or whitespace anywhere in the path
  // eslint-disable-next-line no-control-regex
  if (/[\s\x00-\x1f\x7f]/.test(decoded)) return null;

  // --- Round 4: path-specific rules ---
  // Extract path portion (without query/hash) for route-level checks
  const pathOnly = decoded.split('?')[0].split('#')[0];

  // Reject login — would cause a login-loop
  if (pathOnly === '/login' || pathOnly === '/') return null;

  // Return the ORIGINAL (pre-decoded) value so query strings with encoded
  // characters (e.g. search terms) are faithfully preserved by the browser.
  return trimmed;
}

// ─── Component ────────────────────────────────────────────────────────────────
/**
 * SessionExpiredDialog
 *
 * Global modal shown when the server definitively invalidates the session.
 * This is a UX layer only — the backend remains authoritative for all security.
 *
 * Key design decisions (per review):
 *  • No auto-redirect: stays open until the user explicitly acts.
 *  • Elapsed timer (awareness) instead of countdown-to-force.
 *  • Return URL validated as a same-origin path (sanitizeReturnPath).
 *  • BroadcastChannel syncs expiry state across tabs; distinct message types
 *    prevent logout events being mistaken for session restoration.
 *  • SESSION_RESTORED from another tab triggers a page reload (not just
 *    dialog close) so the current tab gets fresh server-side auth state via
 *    the shared httpOnly refresh-token cookie.
 *  • Component teardown closes the channel and clears all timers.
 *
 * Mount once inside <Router> (AppRoutes) so it has navigation context.
 */
const SessionExpiredDialog = ({ onLoginClick }) => {
  const [open, setOpen]         = useState(false);
  const [reason, setReason]     = useState('expired');
  const [elapsedSecs, setElapsed] = useState(0);

  const channelRef    = useRef(null);
  const elapsedTimer  = useRef(null);
  // Prevent duplicate dialog opens from rapid-fire events (e.g. concurrent 401s)
  const openedRef     = useRef(false);

  // ── BroadcastChannel setup ─────────────────────────────────────────────
  useEffect(() => {
    try {
      const ch = new BroadcastChannel('flux_session');

      ch.onmessage = (e) => {
        const { type, tabId, reason: bcReason } = e.data || {};

        // Safety: skip messages we somehow sent ourselves
        if (tabId === TAB_ID) return;

        if (type === BC_TYPE_EXPIRED) {
          // Another tab detected session expiry — show dialog here too
          handleOpen(bcReason || 'expired');
        } else if (type === BC_TYPE_RESTORED) {
          // Another tab successfully logged in. The refresh-token cookie is now
          // valid again. Dismiss the dialog and reload so this tab re-authenticates
          // via its own AuthContext.rehydrate() flow. This ensures the current
          // tab receives authoritative server state (correct company, permissions)
          // rather than blindly trusting the other tab's in-memory context.
          setOpen(false);
          openedRef.current = false;
          window.location.reload();
        }
        // Note: we do NOT handle logout as SESSION_RESTORED. AuthContext fires
        // SESSION_RESTORED only after a genuinely successful login, not on logout.
      };

      channelRef.current = ch;
    } catch {
      // BroadcastChannel unavailable (e.g. some WebViews) — single-tab mode
    }

    return () => {
      try { channelRef.current?.close(); } catch { /* ignore */ }
      channelRef.current = null;
    };
  }, []);

  // ── Broadcast helpers ──────────────────────────────────────────────────
  const broadcast = useCallback((type, payload = {}) => {
    try {
      channelRef.current?.postMessage({ type, tabId: TAB_ID, ...payload });
    } catch { /* ignore */ }
  }, []);

  // ── Open the dialog ────────────────────────────────────────────────────
  const handleOpen = useCallback((why) => {
    // Deduplicate: don't reset elapsed timer if already open
    if (openedRef.current) return;
    openedRef.current = true;
    setReason(why || 'expired');
    setElapsed(0);
    setOpen(true);
  }, []);

  // ── Event listeners (window events for same-tab, channel for cross-tab) ─
  useEffect(() => {
    const onExpired  = (e) => handleOpen(e.detail?.reason);
    const onRestored = () => {
      // Same tab restored (e.g. user logged in from a non-dialog flow) — just close
      setOpen(false);
      openedRef.current = false;
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    window.addEventListener(SESSION_RESTORED_EVENT, onRestored);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
      window.removeEventListener(SESSION_RESTORED_EVENT, onRestored);
    };
  }, [handleOpen]);

  // ── Broadcast to other tabs when this tab opens the dialog ────────────
  useEffect(() => {
    if (open) broadcast(BC_TYPE_EXPIRED, { reason });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Elapsed timer (awareness only — not a redirect countdown) ─────────
  useEffect(() => {
    if (open) {
      elapsedTimer.current = setInterval(() => setElapsed(s => s + 1), 1000);
    } else {
      clearInterval(elapsedTimer.current);
    }
    return () => clearInterval(elapsedTimer.current);
  }, [open]);

  const formatElapsed = (s) => {
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60), r = s % 60;
    return r === 0 ? `${m}m` : `${m}m ${r}s`;
  };

  // ── Sign-in action ─────────────────────────────────────────────────────
  const handleLogin = useCallback(() => {
    setOpen(false);
    openedRef.current = false;

    if (onLoginClick) {
      onLoginClick();
      return;
    }

    // Capture the current path (full: pathname + search + hash) and validate
    // it as a safe internal route before storing. sanitizeReturnPath() rejects
    // external URLs, encoded redirect attacks, and the login page itself.
    const rawPath = window.location.pathname + window.location.search + window.location.hash;
    const safePath = sanitizeReturnPath(rawPath);
    if (safePath) {
      sessionStorage.setItem('flux_return_after_login', safePath);
    } else {
      sessionStorage.removeItem('flux_return_after_login');
    }

    // Hard navigate to login. Using window.location.assign (same origin only)
    // rather than window.location.href avoids any open-redirect risk from
    // external input. React Router navigation is not available here because the
    // dialog mounts outside a stable router scope.
    window.location.assign('/login');
  }, [onLoginClick]);

  // ── UI copy per reason ─────────────────────────────────────────────────
  const copy = {
    inactivity: {
      title:    'Your session timed out',
      subtitle: 'You were signed out after a period of inactivity to keep your account secure.',
      badge:    'Inactivity Timeout',
    },
    revoked: {
      title:    'Session ended by administrator',
      subtitle: 'Your session was ended remotely. This can happen when your access level changes or you sign in on another device.',
      badge:    'Session Revoked',
    },
    expired: {
      title:    'Your session has expired',
      subtitle: 'For your security, sessions expire automatically. Please sign in again to continue.',
      badge:    'Session Expired',
    },
  };
  const { title, subtitle, badge } = copy[reason] || copy.expired;

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      onClose={() => {}} // intentionally blocked — user must act
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 400 }}
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          border: (t) => `1px solid ${t.palette.divider}`,
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0,0,0,0.55)' },
        },
      }}
    >
      {/* Decorative accent line */}
      <LinearProgress variant="indeterminate" sx={{ height: 3, borderRadius: 0 }} />

      <DialogContent sx={{ px: 4, pt: 4, pb: 4, textAlign: 'center' }}>
        {/* Icon */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <Avatar
            sx={{
              width: 72, height: 72,
              bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(244,67,54,0.15)' : 'rgba(244,67,54,0.08)',
              border: '2px solid', borderColor: 'error.main',
            }}
          >
            <LockClockIcon sx={{ fontSize: 38, color: 'error.main' }} />
          </Avatar>
        </Box>

        {/* Badge */}
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(244,67,54,0.12)' : 'rgba(244,67,54,0.07)',
          color: 'error.main', px: 1.5, py: 0.5, borderRadius: 100, mb: 2,
        }}>
          <SecurityIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
            {badge}
          </Typography>
        </Box>

        {/* Title + subtitle */}
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, letterSpacing: -0.3 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.7 }}>
          {subtitle}
        </Typography>

        {/* Unsaved-work notice — explains that this tab stays intact */}
        <Alert
          severity="warning"
          icon={<WarningIcon fontSize="small" />}
          sx={{
            textAlign: 'left', mb: 3, borderRadius: 2,
            '& .MuiAlert-message': { fontSize: '0.78rem' },
          }}
        >
          <strong>This page is still open.</strong> Any unsaved changes remain
          in this tab. To copy your work before signing in, open a new tab —
          but note that a new tab will also require authentication.
        </Alert>

        <Divider sx={{ mb: 3 }} />

        {/* CTA */}
        <Button
          fullWidth variant="contained" size="large"
          startIcon={<LoginIcon />} onClick={handleLogin}
          sx={{
            borderRadius: 2.5, fontWeight: 700, textTransform: 'none',
            fontSize: '1rem', py: 1.25, boxShadow: 'none',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.18)' },
          }}
        >
          Sign In Again
        </Button>

        {/* Elapsed time — awareness only, NOT a redirect countdown */}
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
          Session ended{' '}
          <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {formatElapsed(elapsedSecs)}
          </Box>
          {' '}ago · This dialog will not close automatically
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default SessionExpiredDialog;
